import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import publicJobService from '../../services/publicJobService';
import Icon from '../../components/AppIcon';
import PublicHeader from '../../components/ui/PublicHeader';
import PublicFooter from '../../components/ui/PublicFooter';

const ApplicationTracker = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the public endpoint to track applications by email
      let data;
      if (user && user.email) {
        // If user is logged in, use their email
        data = await publicJobService.trackApplications(user.email);
      } else {
        // If user is not logged in, we would need to get email from input
        // For now, we'll show an empty state
        data = { applications: [] };
      }
      
      const apps = data?.applications || [];
      setApplications(Array.isArray(apps) ? apps : []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setError('Failed to load applications. Please try again later.');
      
      // Fallback to empty array
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusTexts = {
      'uploaded': 'Application Submitted',
      'processing': 'Under Review',
      'completed': 'Review Complete',
      'in_review': 'In Review',
      'accepted': 'Accepted',
      'rejected': 'Not Selected',
      'pending': 'Pending'
    };

    const statusColors = {
      'uploaded': 'bg-blue-100 text-blue-800',
      'processing': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'in_review': 'bg-blue-100 text-blue-800',
      'accepted': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'pending': 'bg-gray-100 text-gray-800'
    };

    const displayText = statusTexts[status] || status;
    const colorClass = statusColors[status] || statusColors.pending;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {displayText}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <PublicHeader 
          currentUser={user}
          showAuthButtons={false}
          showNavigation={true}
        />
        
        {/* Main Content */}
        <div className="flex-1 max-w-6xl mx-auto p-6 w-full">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader" size={32} className="animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading your applications...</p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PublicHeader 
        currentUser={user}
        showAuthButtons={false}
        showNavigation={true}
      />
      
      {/* Main Content */}
      <div className="flex-1 max-w-6xl mx-auto p-6 w-full">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Applications</h1>
          <p className="text-gray-600 mt-1">Track the status of your job applications</p>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} color="#dc2626" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={loadApplications}
                  className="mt-2 text-sm text-red-700 font-medium hover:text-red-800"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}
        
        {applications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Icon name="FileText" size={48} className="text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
            <p className="text-gray-600 mb-6">You haven't applied to any jobs yet.</p>
            <a 
              href="/public/jobs" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Icon name="Search" size={16} className="mr-2" />
              Browse Jobs
            </a>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Application History ({applications.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-200">
              {applications.map(app => (
                <div key={app.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Icon name="Briefcase" size={24} color="#3b82f6" />
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {app.job_title || 'Untitled Position'}
                          </h3>
                          <p className="text-gray-600 mt-1">
                            {app.company_name || 'Company Not Specified'}
                          </p>
                          
                          <div className="mt-3 flex flex-wrap gap-4">
                            <div className="flex items-center text-sm text-gray-500">
                              <Icon name="Calendar" size={16} className="mr-1.5" />
                              <span>
                                Applied {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : 'Unknown Date'}
                              </span>
                            </div>
                            
                            {app.ai_score && (
                              <div className="flex items-center text-sm">
                                <Icon name="Star" size={16} className="mr-1.5 text-yellow-500" />
                                <span className="text-gray-700">
                                  AI Score: <span className="font-medium">{Math.round(app.ai_score)}</span>/100
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>
                    
                    <div className="ml-4 flex-shrink-0">
                      {getStatusBadge(app.status)}
                    </div>
                  </div>
                  
                  {app.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-700">{app.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default ApplicationTracker;